/**
 * Motivation Card Utility Functions
 * Helper functions for generating motivational messages and tracking milestones
 */

/**
 * Check if user was active today
 * @param {Object} activities - Activity data keyed by date (YYYY-MM-DD)
 * @returns {boolean} Whether there's activity for today
 */
export function checkIsActiveToday(activities) {
  if (!activities || typeof activities !== 'object') {
    return false
  }
  const today = new Date().toISOString().split('T')[0]
  return activities[today] && activities[today] > 0
}

/**
 * Get motivational message based on streak state
 * @param {number} currentStreak - Current streak in days
 * @param {number} longestStreak - User's longest streak
 * @param {boolean} isActiveToday - Whether user has been active today
 * @returns {string} Motivational message
 */
export function getMotivationMessage(currentStreak, longestStreak, isActiveToday) {
  if (isActiveToday && currentStreak > 0) {
    return 'Great work today! You\'re building strong habits.'
  }
  if (currentStreak === 0) {
    return 'Start a new streak today. Consistency is key to SAT success!'
  }
  if (currentStreak >= longestStreak && longestStreak > 0) {
    return 'You\'re setting a new personal best! Keep going!'
  }
  if (currentStreak >= 7) {
    return 'A full week of practice! Your dedication is paying off.'
  }
  if (currentStreak >= 3) {
    return 'You\'re building momentum. Keep the streak alive!'
  }
  return 'Every day of practice brings you closer to your goal.'
}

/**
 * Get the next milestone target for the user
 * @param {number} currentStreak - Current streak in days
 * @returns {number} Next milestone to aim for
 */
export function getNextMilestone(currentStreak) {
  const milestones = [3, 7, 14, 21, 30, 60, 100]
  return milestones.find(m => m > currentStreak) || currentStreak + 10
}

/**
 * Check if current streak is at a milestone
 * @param {number} currentStreak - Current streak in days
 * @returns {boolean} Whether the streak is at a milestone
 */
export function isAtMilestone(currentStreak) {
  const milestones = [7, 14, 21, 30, 60, 100]
  return milestones.includes(currentStreak)
}

/**
 * Get the card state based on user activity
 * @param {number} currentStreak - Current streak in days
 * @param {number} longestStreak - User's longest streak
 * @param {boolean} isActiveToday - Whether user has been active today
 * @returns {string} Card state: 'milestone' | 'active-today' | 'has-streak' | 'no-streak'
 */
export function getCardState(currentStreak, longestStreak, isActiveToday) {
  if (isAtMilestone(currentStreak) && isActiveToday) {
    return 'milestone'
  }
  if (isActiveToday) {
    return 'active-today'
  }
  if (currentStreak > 0) {
    return 'has-streak'
  }
  return 'no-streak'
}
